import React, { useState } from 'react';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useRefresh,
  useNotify,
  useDataProvider,
  useGetList,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle,
  XCircle,
  FileText,
  Video,
  Award,
  Image as ImageIcon,
  ExternalLink,
  Copy,
  Eye,
  FileEdit,
  Pause,
  Ban,
  RotateCcw,
  History,
  Check,
  X as XIcon,
} from 'lucide-react';

import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog, ConfirmTone } from '../components/ui/confirm-dialog';
import { ADMIN_ACTION_REASON } from '../utils/validators';
import { Avatar, AvatarName } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';
import { logger, LogCategory } from '../utils/logger';

/* ============================================================
   Trainer Applications — Latio design system (DESIGN_SYSTEM.md).
   Re-skin of the review queue: list screen + full-page detail, preserving the
   full status model and all admin actions / dataProvider calls.
   ============================================================ */

interface TrainerApplication {
  id: number;
  user_id: number;
  bio: string;
  id_document_front_url: string;
  id_document_back_url: string;
  video_url: string | null;
  certificate_urls: string;
  class_photo_urls: string | null;
  status:
    | 'PENDING'
    | 'UNDER_REVIEW'
    | 'REQUIRES_RESUBMISSION'
    | 'APPROVED'
    | 'REJECTED'
    | 'SUSPENDED'
    | 'REVOKED'
    | 'BANNED';
  reviewed_by_admin_id: number | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
    username: string;
    profile_picture_url?: string;
  };
}

interface StatusHistoryEntry {
  id: number;
  application_id: number;
  previous_status: string;
  new_status: string;
  changed_by_admin_id: number;
  reason: string | null;
  changed_at: string;
}

/* ---- Status meta (label + tone) ---- */
const APP_STATUS: Record<
  string,
  { label: string; tone: 'indigo' | 'warning' | 'success' | 'error' | 'neutral' }
> = {
  PENDING: { label: 'Pending', tone: 'indigo' },
  UNDER_REVIEW: { label: 'Under review', tone: 'warning' },
  REQUIRES_RESUBMISSION: { label: 'Needs resubmission', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'error' },
  SUSPENDED: { label: 'Suspended', tone: 'error' },
  REVOKED: { label: 'Revoked', tone: 'neutral' },
  BANNED: { label: 'Banned', tone: 'error' },
};

const TONE_CLS: Record<string, string> = {
  indigo: 'bg-indigo-tint text-indigo',
  warning: 'bg-warning-bg text-warning',
  success: 'bg-success-bg text-success',
  error: 'bg-error-bg text-error',
  neutral: 'bg-surface-2 text-ink-2',
};
const TONE_DOT: Record<string, string> = {
  indigo: 'bg-indigo',
  warning: 'bg-warning',
  success: 'bg-success',
  error: 'bg-error',
  neutral: 'bg-ink-3',
};

function AppStatusBadge({ status }: { status?: string }) {
  const meta = APP_STATUS[status || ''] ?? { label: status || '—', tone: 'neutral' as const };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold',
        TONE_CLS[meta.tone],
      )}
    >
      <span className={cn('size-1.5 rounded-full', TONE_DOT[meta.tone])} aria-hidden />
      {meta.label}
    </span>
  );
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

/* ---- Pill button ---- */
function PillBtn({
  children,
  onClick,
  variant = 'outline',
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'outline' | 'danger' | 'success';
  disabled?: boolean;
}) {
  const styles = {
    primary: 'bg-black text-white hover:bg-ink',
    success: 'bg-success text-white hover:bg-success/90',
    danger: 'bg-error text-white hover:bg-error/90',
    outline: 'border border-hairline text-ink hover:bg-surface-2',
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-pill px-4 text-sm font-semibold transition-colors duration-150 disabled:opacity-50',
        styles,
      )}
    >
      {children}
    </button>
  );
}

/* ---- Detail card ---- */
function DetailCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

/* ============================================================
   Media viewer (lightbox) + image gallery
   ============================================================ */
const MediaViewerModal = ({
  src,
  alt,
  type = 'image',
  open,
  onClose,
}: {
  src: string;
  alt: string;
  type?: 'image' | 'video';
  open: boolean;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error(LogCategory.UI, 'Failed to copy text', err, 'TrainerApplications');
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <span>{type === 'video' ? 'Video' : 'Image'}</span>
          <button onClick={onClose} className="text-ink-2 hover:text-ink">
            ✕
          </button>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4">
          <div className="flex justify-center">
            {type === 'video' ? (
              <video src={src} controls className="max-h-96 max-w-full rounded-card">
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={src}
                alt={alt}
                className="max-h-96 max-w-full rounded-card object-contain"
                onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
              />
            )}
          </div>
          <div className="flex items-center gap-2 rounded-input border border-hairline bg-surface-2 p-2">
            <code className="flex-1 break-all text-sm text-ink-2">{src}</code>
            <button
              onClick={() => copyToClipboard(src)}
              className="flex items-center justify-center rounded border border-hairline p-1 hover:bg-background"
              title="Copy URL"
            >
              <Copy className="size-3" />
            </button>
            <button
              onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
              className="flex items-center justify-center rounded border border-hairline p-1 hover:bg-background"
              title="Open in new tab"
            >
              <ExternalLink className="size-3" />
            </button>
          </div>
          {copied && <p className="text-xs text-success">URL copied to clipboard!</p>}
        </div>
      </DialogContent>
      <DialogActions>
        <PillBtn onClick={onClose}>Close</PillBtn>
      </DialogActions>
    </Dialog>
  );
};

const ImageGallery = ({ urls, title }: { urls: string[]; title: string }) => {
  const [selected, setSelected] = useState<string | null>(null);
  if (!urls || urls.length === 0) {
    return <p className="text-sm text-ink-2">No {title.toLowerCase()} provided</p>;
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {urls.map((url, i) => (
          <div
            key={i}
            className="relative aspect-square cursor-pointer overflow-hidden rounded-latio-sm border border-hairline"
            onClick={() => setSelected(url)}
          >
            <img
              src={url}
              alt={`${title} ${i + 1}`}
              className="size-full object-cover"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          </div>
        ))}
      </div>
      {selected && (
        <MediaViewerModal src={selected} alt={title} open={!!selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

/* ============================================================
   LIST
   ============================================================ */
const STATUS_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'REQUIRES_RESUBMISSION', label: 'Resubmission' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'REVOKED', label: 'Revoked' },
  { value: 'BANNED', label: 'Banned' },
];

const SORT_FIELDS = [
  { field: 'created_at', label: 'Applied' },
  { field: 'reviewed_at', label: 'Reviewed' },
  { field: 'status', label: 'Status' },
  { field: 'id', label: 'ID' },
];
const SORT_LABELS = Object.fromEntries(SORT_FIELDS.map((f) => [f.field, f.label]));

function StatusTabs() {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status_filter as string) || 'ALL';
  return (
    <div className="max-w-full overflow-x-auto">
      <SegmentedTabs
        tabs={STATUS_TABS}
        value={current}
        onChange={(v) =>
          setFilters({ ...filterValues, status_filter: v === 'ALL' ? undefined : v }, displayedFilters)
        }
      />
    </div>
  );
}

function ApplicationStats() {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const pending = useGetList('trainer-applications', { ...base, filter: { status_filter: 'PENDING' } });
  const review = useGetList('trainer-applications', { ...base, filter: { status_filter: 'UNDER_REVIEW' } });
  const approved = useGetList('trainer-applications', { ...base, filter: { status_filter: 'APPROVED' } });
  const all = useGetList('trainer-applications', { ...base, filter: {} });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Pending review" value={n(pending.total)} />
      <StatCard label="Under review" value={n(review.total)} />
      <StatCard label="Approved" value={n(approved.total)} />
      <StatCard label="Total applications" value={n(all.total)} />
    </StatRow>
  );
}

function ApplicationsListBody() {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  const [rejectTarget, setRejectTarget] = useState<TrainerApplication | null>(null);

  const approve = async (record: TrainerApplication) => {
    try {
      await dataProvider.update('trainer-applications', {
        id: record.id,
        data: { approved: true },
        previousData: record,
      });
      toastSuccess('Application approved', record.user?.name);
      refresh();
    } catch {
      toastError('Failed to approve application');
    }
  };

  const reject = async (reason?: string) => {
    if (!rejectTarget) return;
    try {
      await dataProvider.update('trainer-applications', {
        id: rejectTarget.id,
        data: { approved: false, rejection_reason: reason },
        previousData: rejectTarget,
      });
      toastSuccess('Application rejected', rejectTarget.user?.name);
      refresh();
    } catch {
      toastError('Failed to reject application');
    }
  };

  const columns: DataColumn<TrainerApplication>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '1px',
      cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span>,
    },
    {
      key: 'applicant',
      header: 'Applicant',
      cell: (r) => (
        <AvatarName src={r.user?.profile_picture_url} name={r.user?.name || 'Unknown'} subtext={r.user?.email} />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      cell: (r) => <AppStatusBadge status={r.status} />,
    },
    { key: 'created_at', header: 'Applied', sortable: true, cell: (r) => fmtDate(r.created_at) },
    { key: 'reviewed_at', header: 'Reviewed', sortable: true, cell: (r) => fmtDate(r.reviewed_at) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) =>
        r.status === 'PENDING' || r.status === 'UNDER_REVIEW' ? (
          <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => approve(r)}
              title="Approve"
              className="flex size-8 items-center justify-center rounded-pill border border-hairline text-success hover:bg-success-bg"
            >
              <Check className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setRejectTarget(r)}
              title="Reject"
              className="flex size-8 items-center justify-center rounded-pill border border-hairline text-error hover:bg-error-bg"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <ListScreen
        stats={<ApplicationStats />}
        tabs={<StatusTabs />}
        actions={<SortMenu fields={SORT_FIELDS} />}
        tokens={<FilterTokens fields={[]} sortLabels={SORT_LABELS} />}
      >
        <DataTable<TrainerApplication> columns={columns} rowClick="show" emptyMessage="No applications found." />
        <div className="pt-1">
          <ReactAdminPagination />
        </div>
      </ListScreen>

      <ConfirmDialog
        open={!!rejectTarget}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        tone="destructive"
        title="Reject application"
        consequence="The applicant will see your reason (Motivo). This can be reconsidered later."
        confirmLabel="Reject application"
        reasonRequired
        reasonChips={['Incomplete documentation', 'Credentials not verifiable', 'Does not meet requirements']}
        onConfirm={reject}
      />
    </>
  );
}

export const TrainerApplicationList = (props: any) => (
  <List
    {...props}
    actions={false}
    empty={false}
    pagination={false}
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
    component="div"
  >
    <ApplicationsListBody />
  </List>
);

/* ============================================================
   DETAIL
   ============================================================ */
type ConfirmConfig = {
  title: string;
  consequence?: string;
  tone: ConfirmTone;
  confirmLabel: string;
  reasonRequired?: boolean;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  reasonChips?: string[];
  run: (reason?: string) => Promise<void>;
};

function VerificationChecklist({ record }: { record: TrainerApplication }) {
  const certs = record.certificate_urls ? safeParse(record.certificate_urls) : [];
  const checks = [
    { label: 'ID document (front)', ok: !!record.id_document_front_url },
    { label: 'ID document (back)', ok: !!record.id_document_back_url },
    { label: 'Introduction video', ok: !!record.video_url },
    { label: 'Certificates', ok: certs.length > 0 },
    { label: 'Bio provided', ok: !!record.bio },
  ];
  return (
    <ul className="space-y-2">
      {checks.map((c) => (
        <li key={c.label} className="flex items-center gap-2 text-sm">
          <span
            className={cn(
              'flex size-5 items-center justify-center rounded-pill',
              c.ok ? 'bg-success-bg text-success' : 'bg-surface-2 text-ink-3',
            )}
          >
            {c.ok ? <Check className="size-3" /> : <XIcon className="size-3" />}
          </span>
          <span className={c.ok ? 'text-ink' : 'text-ink-2'}>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

function safeParse(json: string): string[] {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

const TrainerApplicationShowContent = () => {
  const { record, isLoading } = useShowContext<TrainerApplication>();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();

  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [idFrontOpen, setIdFrontOpen] = useState(false);
  const [idBackOpen, setIdBackOpen] = useState(false);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading application…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Application not found</div>;

  const certs = record.certificate_urls ? safeParse(record.certificate_urls) : [];
  const classPhotos = record.class_photo_urls ? safeParse(record.class_photo_urls) : [];

  const run = (fn: () => Promise<any>, ok: string, fail: string) => async (reason?: string) => {
    try {
      await fn();
      toastSuccess(ok);
      refresh();
    } catch {
      toastError(fail);
    }
    void reason;
  };

  const approve = run(
    () => dataProvider.update('trainer-applications', { id: record.id, data: { approved: true }, previousData: record }),
    'Application approved',
    'Failed to approve',
  );
  const markUnderReview = run(
    () => dataProvider.markApplicationUnderReview({ id: record.id }),
    'Marked under review',
    'Failed to update',
  );
  const reinstate = run(
    () => dataProvider.reinstateApplication({ id: record.id }),
    'Trainer reinstated',
    'Failed to reinstate',
  );

  const openHistory = async () => {
    try {
      const { data } = await dataProvider.getApplicationHistory({ id: record.id });
      setHistory(data || []);
      setHistoryOpen(true);
    } catch {
      notify('Failed to fetch history', { type: 'error' });
    }
  };

  // Confirm-with-reason actions
  const rejectCfg: ConfirmConfig = {
    title: 'Reject application',
    consequence: 'The applicant will see your reason (Motivo).',
    tone: 'destructive',
    confirmLabel: 'Reject',
    reasonRequired: true,
    reasonChips: ['Incomplete documentation', 'Credentials not verifiable', 'Does not meet requirements'],
    run: async (reason) => {
      await run(
        () =>
          dataProvider.update('trainer-applications', {
            id: record.id,
            data: { approved: false, rejection_reason: reason },
            previousData: record,
          }),
        'Application rejected',
        'Failed to reject',
      )(reason);
    },
  };
  const resubmissionCfg: ConfirmConfig = {
    title: 'Request resubmission',
    consequence: 'The applicant will be asked to resubmit with your notes.',
    tone: 'warning',
    confirmLabel: 'Request resubmission',
    reasonRequired: true,
    ...ADMIN_ACTION_REASON,
    run: async (reason) =>
      run(
        () => dataProvider.requestApplicationResubmission({ id: record.id, reason: reason!, notes: '' }),
        'Resubmission requested',
        'Failed to request resubmission',
      )(reason),
  };
  const suspendCfg: ConfirmConfig = {
    title: 'Suspend trainer',
    consequence: 'The trainer loses their role until reinstated.',
    tone: 'warning',
    confirmLabel: 'Suspend',
    reasonRequired: true,
    ...ADMIN_ACTION_REASON,
    run: async (reason) =>
      run(
        () => dataProvider.suspendApplication({ id: record.id, reason: reason!, notes: '' }),
        'Trainer suspended',
        'Failed to suspend',
      )(reason),
  };
  const revokeCfg: ConfirmConfig = {
    title: 'Revoke permanently',
    consequence: 'The trainer role is removed and the application is marked REVOKED.',
    tone: 'destructive',
    confirmLabel: 'Revoke',
    reasonRequired: true,
    ...ADMIN_ACTION_REASON,
    run: async (reason) =>
      run(
        () => dataProvider.revokeApplication({ id: record.id, reason: reason!, notes: '' }),
        'Trainer revoked',
        'Failed to revoke',
      )(reason),
  };
  const banCfg: ConfirmConfig = {
    title: 'Ban application',
    consequence: 'The user can no longer submit trainer applications. Most severe action.',
    tone: 'destructive',
    confirmLabel: 'Ban',
    reasonRequired: true,
    ...ADMIN_ACTION_REASON,
    run: async (reason) =>
      run(
        () => dataProvider.banApplication({ id: record.id, reason: reason!, notes: '' }),
        'Application banned',
        'Failed to ban',
      )(reason),
  };

  const s = record.status;
  return (
    <div className="space-y-6 p-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={record.user?.profile_picture_url} name={record.user?.name} size={60} />
          <div>
            <div className="flex items-center gap-3">
              <h1
                className="text-[24px] font-bold leading-none text-ink"
                style={{ letterSpacing: '-0.02em' }}
              >
                {record.user?.name || 'Unknown User'}
              </h1>
              <AppStatusBadge status={record.status} />
            </div>
            <p className="mt-1 text-sm text-ink-2">
              {record.user?.email}
              {record.user?.username && ` · @${record.user.username}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <PillBtn onClick={openHistory}>
            <History className="size-4" />
            History
          </PillBtn>

          {(s === 'PENDING' || s === 'UNDER_REVIEW') && (
            <>
              {s === 'PENDING' && (
                <PillBtn onClick={() => setConfirm({
                  title: 'Mark under review', tone: 'indigo', confirmLabel: 'Mark under review',
                  consequence: 'Move this application into the review state.', run: markUnderReview,
                })}>
                  <Eye className="size-4" />
                  Under review
                </PillBtn>
              )}
              <PillBtn variant="outline" onClick={() => setConfirm(resubmissionCfg)}>
                <FileEdit className="size-4" />
                Resubmission
              </PillBtn>
              <PillBtn variant="danger" onClick={() => setConfirm(rejectCfg)}>
                <XCircle className="size-4" />
                Reject
              </PillBtn>
              <PillBtn variant="primary" onClick={() => setConfirm({
                title: 'Approve coach', tone: 'indigo', confirmLabel: 'Approve',
                consequence: 'Grant trainer privileges to this user.', run: approve,
              })}>
                <CheckCircle className="size-4" />
                Approve coach
              </PillBtn>
            </>
          )}

          {s === 'APPROVED' && (
            <>
              <PillBtn variant="outline" onClick={() => setConfirm(suspendCfg)}>
                <Pause className="size-4" />
                Suspend
              </PillBtn>
              <PillBtn variant="danger" onClick={() => setConfirm(revokeCfg)}>
                <XCircle className="size-4" />
                Revoke
              </PillBtn>
            </>
          )}

          {s === 'SUSPENDED' && (
            <>
              <PillBtn variant="success" onClick={() => setConfirm({
                title: 'Reinstate trainer', tone: 'indigo', confirmLabel: 'Reinstate',
                consequence: 'Restore the trainer role.', run: reinstate,
              })}>
                <RotateCcw className="size-4" />
                Reinstate
              </PillBtn>
              <PillBtn variant="danger" onClick={() => setConfirm(revokeCfg)}>
                <XCircle className="size-4" />
                Revoke
              </PillBtn>
            </>
          )}

          {!['BANNED', 'REVOKED'].includes(s) && (
            <PillBtn variant="outline" onClick={() => setConfirm(banCfg)}>
              <Ban className="size-4" />
              Ban
            </PillBtn>
          )}
        </div>
      </div>

      {/* Rejection reason banner */}
      {record.rejection_reason && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>Motivo:</strong> {record.rejection_reason}
        </div>
      )}

      {/* Two-column body */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Main */}
        <div className="space-y-6">
          <DetailCard title="About" icon={FileText}>
            <p className="whitespace-pre-wrap text-sm text-ink">{record.bio || 'No bio provided'}</p>
          </DetailCard>

          <DetailCard title="Documents" icon={ImageIcon}>
            <div className="space-y-5">
              {/* ID document */}
              <div>
                <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
                  ID document
                </div>
                {record.id_document_front_url || record.id_document_back_url ? (
                  <div className="flex flex-wrap gap-3">
                    {record.id_document_front_url && (
                      <img
                        src={record.id_document_front_url}
                        alt="ID front"
                        onClick={() => setIdFrontOpen(true)}
                        className="h-28 cursor-pointer rounded-latio-sm border border-hairline object-cover"
                      />
                    )}
                    {record.id_document_back_url && (
                      <img
                        src={record.id_document_back_url}
                        alt="ID back"
                        onClick={() => setIdBackOpen(true)}
                        className="h-28 cursor-pointer rounded-latio-sm border border-hairline object-cover"
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-ink-2">No ID document provided</p>
                )}
              </div>

              {/* Video */}
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
                  <Video className="size-3.5" /> Introduction video
                </div>
                {record.video_url ? (
                  <button
                    type="button"
                    onClick={() => setVideoOpen(true)}
                    className="inline-flex items-center gap-2 rounded-pill border border-hairline px-3 py-1.5 text-sm font-semibold text-ink hover:bg-surface-2"
                  >
                    <ExternalLink className="size-4" /> Play video
                  </button>
                ) : (
                  <p className="text-sm text-ink-2">No video provided</p>
                )}
              </div>

              {/* Certificates */}
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
                  <Award className="size-3.5" /> Certificates
                </div>
                <ImageGallery urls={certs} title="Certificates" />
              </div>

              {/* Class photos */}
              <div>
                <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
                  Class photos
                </div>
                <ImageGallery urls={classPhotos} title="Class photos" />
              </div>
            </div>
          </DetailCard>
        </div>

        {/* Aside */}
        <div className="space-y-6">
          <DetailCard title="Verification" icon={CheckCircle}>
            <VerificationChecklist record={record} />
          </DetailCard>

          <DetailCard title="Application">
            <div className="divide-y divide-hairline">
              <Fact label="Application ID" value={`#${record.id}`} />
              <Fact label="User ID" value={record.user_id} />
              <Fact label="Status" value={<AppStatusBadge status={record.status} />} />
              <Fact label="Submitted" value={fmtDate(record.created_at)} />
              <Fact label="Reviewed" value={fmtDate(record.reviewed_at)} />
            </div>
          </DetailCard>
        </div>
      </div>

      {/* Confirm dialog (shared) */}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title || ''}
        consequence={confirm?.consequence}
        tone={confirm?.tone || 'destructive'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        reasonRequired={confirm?.reasonRequired}
        reasonMinLength={confirm?.reasonMinLength}
        reasonMaxLength={confirm?.reasonMaxLength}
        reasonChips={confirm?.reasonChips}
        onConfirm={async (reason) => {
          if (confirm) await confirm.run(reason);
        }}
      />

      {/* Media lightboxes */}
      {record.video_url && (
        <MediaViewerModal src={record.video_url} alt="Video" type="video" open={videoOpen} onClose={() => setVideoOpen(false)} />
      )}
      {record.id_document_front_url && (
        <MediaViewerModal src={record.id_document_front_url} alt="ID front" open={idFrontOpen} onClose={() => setIdFrontOpen(false)} />
      )}
      {record.id_document_back_url && (
        <MediaViewerModal src={record.id_document_back_url} alt="ID back" open={idBackOpen} onClose={() => setIdBackOpen(false)} />
      )}

      {/* History dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Application status history</DialogTitle>
        <DialogContent>
          <div className="space-y-3 pt-2">
            {history.length === 0 ? (
              <p className="py-4 text-center text-sm text-ink-2">No status history available</p>
            ) : (
              history.map((entry) => (
                <div key={entry.id} className="rounded-card border border-hairline p-4">
                  <div className="flex items-center gap-2">
                    <AppStatusBadge status={entry.previous_status} />
                    <span className="text-ink-3">→</span>
                    <AppStatusBadge status={entry.new_status} />
                  </div>
                  {entry.reason && (
                    <p className="mt-2 text-sm text-ink">
                      <span className="font-semibold">Reason: </span>
                      {entry.reason}
                    </p>
                  )}
                  <div className="mt-2 flex gap-4 text-xs text-ink-2">
                    <span>Admin #{entry.changed_by_admin_id}</span>
                    <span>{new Date(entry.changed_at).toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <PillBtn onClick={() => setHistoryOpen(false)}>Close</PillBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const TrainerApplicationShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <TrainerApplicationShowContent />
  </Show>
);
